export type LngLatTuple = [number, number];

export const EARTH_METERS_PER_DEG_LAT = 111320;

export const metersPerDegreeLon = (lat: number) => {
  const radians = (lat * Math.PI) / 180;
  const cos = Math.cos(radians);
  return Math.max(1e-6, Math.abs(cos)) * EARTH_METERS_PER_DEG_LAT;
};

export const normalizeVector = ([x, y]: [number, number]): [number, number] => {
  const length = Math.hypot(x, y);
  if (!Number.isFinite(length) || length === 0) {
    return [0, 0];
  }
  return [x / length, y / length];
};

export const computePolygonCentroid = (
  coordinates: LngLatTuple[],
): LngLatTuple | null => {
  if (coordinates.length === 0) {
    return null;
  }

  let twiceArea = 0;
  let centroidX = 0;
  let centroidY = 0;

  for (let i = 0; i < coordinates.length; i += 1) {
    const [x0, y0] = coordinates[i];
    const [x1, y1] = coordinates[(i + 1) % coordinates.length];
    const cross = x0 * y1 - x1 * y0;
    twiceArea += cross;
    centroidX += (x0 + x1) * cross;
    centroidY += (y0 + y1) * cross;
  }

  if (Math.abs(twiceArea) < 1e-12) {
    const [sumX, sumY] = coordinates.reduce(
      (acc, [x, y]) => [acc[0] + x, acc[1] + y],
      [0, 0] as [number, number],
    );
    return [sumX / coordinates.length, sumY / coordinates.length];
  }

  const area = twiceArea * 0.5;
  return [centroidX / (6 * area), centroidY / (6 * area)];
};

export const offsetTowards = (
  from: LngLatTuple,
  to: LngLatTuple,
  {
    ratio = 0.4,
    min = 0.00003,
    max = 0.00015,
  }: { ratio?: number; min?: number; max?: number } = {},
): LngLatTuple => {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const distance = Math.sqrt(dx * dx + dy * dy);

  if (!Number.isFinite(distance) || distance === 0) {
    return from;
  }

  const step = Math.min(Math.max(distance * ratio, min), max);
  const factor = step / distance;
  return [from[0] + dx * factor, from[1] + dy * factor];
};

export const offsetMetersFromPivot = (
  point: LngLatTuple,
  pivot: LngLatTuple | null,
  meters: number,
): LngLatTuple => {
  if (!pivot || meters <= 0) {
    return point;
  }

  const lat = point[1];
  const mPerLon = metersPerDegreeLon(lat);
  const dx = (point[0] - pivot[0]) * mPerLon;
  const dy = (point[1] - pivot[1]) * EARTH_METERS_PER_DEG_LAT;
  const magnitude = Math.sqrt(dx * dx + dy * dy);
  if (!Number.isFinite(magnitude) || magnitude === 0) {
    return point;
  }

  const scale = meters / magnitude;
  const offsetX = dx * scale;
  const offsetY = dy * scale;
  return [
    point[0] + offsetX / mPerLon,
    point[1] + offsetY / EARTH_METERS_PER_DEG_LAT,
  ];
};

export const haversineMeters = (start: [number, number], end: [number, number]) => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const R = 6371000; // meters
  const dLat = toRad(end[1] - start[1]);
  const dLon = toRad(end[0] - start[0]);
  const lat1 = toRad(start[1]);
  const lat2 = toRad(end[1]);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};
